import React from "react";
import { useLocation, Switch, Route, useHistory } from "react-router-dom";
import { useSeo } from "../../hooks";
import LatestAnnouncement from "../../components/Home/LatestAnnouncement";

import VerifyEmail from "./VerifyEmail";
import HomeLogin from "./HomeLogin";
import RecoverPassword from "./RecoverPassword";
import ResetPassword from "./ResetPassword";

const Home: React.FC<{}> = () => {
    const location = useLocation();
    const history = useHistory();

    useSeo({
        title: "Official Oberin - Fantasy MMORPG for Mac and Windows",
        description:
            "Oberin is a free-to-play fantasy MMORPG for Mac and Windows 8.1, 10, and 11. Explore classes, quests, skills, towns, dungeons, creatures, lore, and a persistent online world. Download the game and join the community today.",
        canonical: "https://www.oberin.be/home",
        robots: "index,follow",
        ogImage: "https://www.oberin.be/logo512.png",
        twitterCard: "summary_large_image",
    });

    const handleNavigate = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        path: string,
    ) => {
        event.preventDefault();
        history.push(path);
    };

    return (
        <React.Fragment>
            <Switch>
                <Route path="/home/verifyEmail">
                    <VerifyEmail pathname={location.pathname} />
                </Route>
                <Route path="/home/login">
                    <HomeLogin />
                </Route>
                <Route path="/home/recoverPassword">
                    <RecoverPassword />
                </Route>
                <Route path="/home/resetPassword">
                    <ResetPassword />
                </Route>
            </Switch>

            <div className="w-100 px-2">
                {/* Hero Section */}
                <div className="box-shadow">
                    <div className="title-bar px-2">
                        <span>Official Oberin</span>
                    </div>
                    <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                        <h1 className="text-center mb-1">Official Oberin</h1>
                        <p className="text-center text-muted mb-2">
                            Fantasy MMORPG and persistent online world
                        </p>
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-6 col-lg-4 mb-2">
                                <a
                                    href="/download"
                                    className="btn dark-red-button text-normal rounded-0 w-100 text-white d-block text-center p-1"
                                    onClick={(e) =>
                                        handleNavigate(e, "/download")
                                    }
                                >
                                    Play the game!
                                </a>
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div className="col-6 col-md-3 mb-2">
                                <a
                                    href="https://discord.gg/oberin"
                                    className="btn dark-red-button text-normal rounded-0 w-100 text-white d-block text-center p-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Join Discord
                                </a>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <a
                                    href="/forum/overview"
                                    className="btn dark-red-button text-normal rounded-0 w-100 text-white d-block text-center p-1"
                                    onClick={(e) =>
                                        handleNavigate(e, "/forum/overview")
                                    }
                                >
                                    Visit Forum
                                </a>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <a
                                    href="https://oberin.fandom.com/wiki/OberinWiki?utm_source=oberin_website"
                                    className="btn dark-red-button text-normal rounded-0 w-100 text-white d-block text-center p-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Explore the Wiki
                                </a>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <a
                                    href="/news"
                                    className="btn dark-red-button text-normal rounded-0 w-100 text-white d-block text-center p-1"
                                    onClick={(e) => handleNavigate(e, "/news")}
                                >
                                    Read the latest news
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Welcome */}
                <div className="mt-2 box-shadow">
                    <div className="title-bar px-2">
                        <span>Welcome to Oberin!</span>
                    </div>
                    <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                        <p>
                            Oberin is a{" "}
                            <strong>free-to-play fantasy MMORPG</strong> set in
                            a persistent, ever-evolving online world. Originally
                            released for Macintosh and now available on modern
                            Mac and Windows systems, Oberin has been running for
                            over two decades, making it one of the
                            longest-running online fantasy worlds still active
                            today.
                        </p>
                        <p>
                            The world of Oberin is shaped by its players.
                            Community events, Real Time Quests led by Game
                            Masters, and player-driven stories create a living
                            history that continues to unfold. Whether you are a
                            veteran adventurer or a newcomer, there is always
                            something new to discover.
                        </p>
                        <p className="mb-0">
                            Oberin is completely free to play with no
                            microtransactions. The journey is the reward.
                        </p>
                    </div>
                </div>

                {/* Most recent announcement */}
                <div className="mt-2 box-shadow">
                    <div className="title-bar px-2">
                        <span>Most recent announcement</span>
                    </div>
                    <div className="bg-light-gray bl-xs br-xs bb-xs">
                        <LatestAnnouncement />
                    </div>
                </div>

                {/* Gameplay */}
                <div className="mt-2 box-shadow">
                    <div className="title-bar px-2">
                        <span>How the Game Plays</span>
                    </div>
                    <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                        <p>
                            Oberin features a blend of combat, exploration, and
                            community interaction:
                        </p>
                        <ul className="mb-0">
                            <li>
                                <strong>Melee, ranged combat, and magic</strong>{" "}
                                — choose your fighting style and develop your
                                skills
                            </li>
                            <li>
                                <strong>Quests and Real Time Quests</strong> —
                                participate in scripted quests and live events
                                run by Game Masters
                            </li>
                            <li>
                                <strong>Guilds and community play</strong> —
                                join a guild, form parties, and take on
                                challenges together
                            </li>
                            <li>
                                <strong>Roleplay and world continuity</strong> —
                                the world has a long-form story that evolves
                                based on player actions and events
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Classes */}
                <div className="mt-2 box-shadow">
                    <div className="title-bar px-2">
                        <span>Classes</span>
                    </div>
                    <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                        <p>
                            Oberin offers 6 distinct classes, each with unique
                            abilities and a range of possible playing styles.
                            Every class has strengths and weaknesses, each can
                            add power to a hunting group, or travel the lands
                            solo in search of treasure and glory.
                        </p>

                        <div className="title-bar dark px-2">
                            <span>Cleric</span>
                        </div>
                        <p className="mt-1">
                            Healer, protector, and scourge of the Undead.
                            Clerics support allies with restorative magic,
                            resurrect the fallen, return the undead to their
                            graves with powerful attacks, and - alone among
                            spellcasters - can wield certain weapons whilst
                            casting.
                            <br />
                            <a
                                href="https://oberin.fandom.com/wiki/Category:Cleric?utm_source=oberin_website"
                                className="text-dark-red"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read more on the wiki →
                            </a>
                        </p>

                        <div className="title-bar dark px-2">
                            <span>Druid</span>
                        </div>
                        <p className="mt-1">
                            Forest dweller, attuned to nature. The Druid bonds
                            deeply with the wild animals of the land, taming
                            them, healing them, and fighting alongside them.
                            Magicians of metamorphosis and movement, they can
                            teleport, become invisible, and even shapeshift into
                            the forms of powerful beasts.
                            <br />
                            <a
                                href="https://oberin.fandom.com/wiki/Category:Druid?utm_source=oberin_website"
                                className="text-dark-red"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read more on the wiki →
                            </a>
                        </p>

                        <div className="title-bar dark px-2">
                            <span>Wizard</span>
                        </div>
                        <p className="mt-1 mb-0">
                            Wielder of arcane power, the Wizard commands
                            devastating magic, poisoning, paralysing, burning
                            and bolting foes. The Wizard can turn the tide of
                            battles, weakening magical enemies, spreading
                            confusion and panic, and attacking entire swarms of
                            enemies at once.
                            <br />
                            <a
                                href="https://oberin.fandom.com/wiki/Category:Wizard?utm_source=oberin_website"
                                className="text-dark-red"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read more on the wiki →
                            </a>
                        </p>

                        <div className="title-bar dark px-2">
                            <span>Fighter</span>
                        </div>
                        <p className="mt-1">
                            Masters of Melee, hard as nails and fierce as
                            dragons, the fighter spearheads the attack. Tough as
                            teak, the fighter is to be found at the forefront of
                            battle, a skilled user of many weapons, able to
                            endure much pain, often the last warrior standing.
                            <br />
                            <a
                                href="https://oberin.fandom.com/wiki/Category:Fighter?utm_source=oberin_website"
                                className="text-dark-red"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read more on the wiki →
                            </a>
                        </p>

                        <div className="title-bar dark px-2">
                            <span>Ranger</span>
                        </div>
                        <p className="mt-1">
                            Deeply wise in the ways of the forest, the ranger
                            tracks nearby beasts, stalking or avoiding them as
                            they choose. Highly skilled with the bow, with which
                            they can unleash terrible special attacks upon
                            enemies, the ranger can also more than hold their
                            own with sword, pole or axe in hand to hand combat.
                            <br />
                            <a
                                href="https://oberin.fandom.com/wiki/Category:Ranger?utm_source=oberin_website"
                                className="text-dark-red"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read more on the wiki →
                            </a>
                        </p>

                        <div className="title-bar dark px-2">
                            <span>Rogue</span>
                        </div>
                        <p className="mt-1">
                            Elusive, stealthy, deadly. The rogue uses speed,
                            precision and cunning to outwit and outmanoeuvre
                            opponents. They can disappear in the blink of an eye
                            and move undetected, open the locks of the most
                            lucrative treasure troves, and kill enemies before
                            they are ever noticed.
                            <br />
                            <a
                                href="https://oberin.fandom.com/wiki/Category:Rogue?utm_source=oberin_website"
                                className="text-dark-red"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read more on the wiki →
                            </a>
                        </p>
                    </div>
                </div>

                {/* World and Systems */}
                <div className="mt-2 box-shadow">
                    <div className="title-bar px-2">
                        <span>World and Systems</span>
                    </div>
                    <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                        <p>
                            The world of Oberin is rich with places to explore
                            and systems to master:
                        </p>
                        <ul className="mb-0">
                            <li>
                                <strong>Towns and cities</strong> — visit
                                Mirith, Andris, Marali, Lerilin, and other
                                settlements
                            </li>
                            <li>
                                <strong>Dungeons</strong> — explore dangerous
                                underground areas filled with creatures and
                                treasure
                            </li>
                            <li>
                                <strong>Locations</strong> — discover forests,
                                mountains, islands, and hidden areas across the
                                map
                            </li>
                            <li>
                                <strong>Items</strong> — collect weapons, armor,
                                potions, and rare magical equipment
                            </li>
                            <li>
                                <strong>Crafting and harvesting</strong> —
                                gather resources and craft items to aid your
                                adventures
                            </li>
                            <li>
                                <strong>Moon cycle and lore</strong> — the world
                                is governed by the cycles of Lady Moon, each
                                bringing different magical influences. Learn
                                more on the{" "}
                                <a
                                    href="/moonCycle"
                                    className="text-dark-red"
                                    onClick={(e) =>
                                        handleNavigate(e, "/moonCycle")
                                    }
                                >
                                    Moon Cycle Calendar
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Community and Resources */}
                <div className="mt-2 box-shadow">
                    <div className="title-bar px-2">
                        <span>Community and Official Resources</span>
                    </div>
                    <div className="bg-light-gray p-2 bl-xs br-xs bb-xs d-flex flex-column">
                        <p>
                            Oberin has a welcoming and active community. Here
                            are the official resources:
                        </p>
                        <ul className="mb-0">
                            <li>
                                <a
                                    href="/download"
                                    className="text-dark-red"
                                    onClick={(e) =>
                                        handleNavigate(e, "/download")
                                    }
                                >
                                    Official Downloads
                                </a>{" "}
                                — download the game and find installation guides
                            </li>
                            <li>
                                <a
                                    href="/forum/overview"
                                    className="text-dark-red"
                                    onClick={(e) =>
                                        handleNavigate(e, "/forum/overview")
                                    }
                                >
                                    Official Forum
                                </a>{" "}
                                — join community discussions, read
                                announcements, and get support
                            </li>
                            <li>
                                <a
                                    href="https://discord.gg/oberin"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Official Discord
                                </a>{" "}
                                — chat with the community in real time
                            </li>
                            <li>
                                <a
                                    href="https://www.facebook.com/oberinmmorpg?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Facebook Page
                                </a>{" "}
                                — follow the official Oberin Facebook page
                            </li>
                            <li>
                                <a
                                    href="https://www.facebook.com/groups/OberinGroup/?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Facebook Community Group
                                </a>{" "}
                                — connect with players on Facebook
                            </li>
                            <li>
                                <a
                                    href="https://www.instagram.com/oberin_mmorpg?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Instagram
                                </a>{" "}
                                — follow Oberin on Instagram
                            </li>
                            <li>
                                <a
                                    href="https://www.reddit.com/r/OberinMMORPG/?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Reddit
                                </a>{" "}
                                — visit the Oberin subreddit
                            </li>
                            <li>
                                <a
                                    href="https://www.youtube.com/@OberinMMORPG?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    YouTube
                                </a>{" "}
                                — watch Oberin videos and event recaps
                            </li>
                            <li>
                                <a
                                    href="https://www.tiktok.com/@oberinmmorpg?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    TikTok
                                </a>{" "}
                                — follow Oberin on TikTok
                            </li>
                            <li>
                                <a
                                    href="https://x.com/oberin_mmorpg?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    X / Twitter
                                </a>{" "}
                                — follow Oberin on X (Twitter)
                            </li>
                            <li>
                                <a
                                    href="https://oberin.fandom.com/wiki/OberinWiki?utm_source=oberin_website"
                                    className="text-dark-red"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Community Wiki
                                </a>{" "}
                                — browse the community-maintained wiki for
                                detailed information
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Home;
